import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import posthogLib from 'posthog-js';

import { symbolKey } from '@/entities/symbol';
import { closeDrawer } from '@/slices/drawerSlice';

import {
  addSymbol,
  addSymbols,
  requestForwardCurveSelection,
  requestSpotSelection,
  restoreSymbols,
  SELECTOR_MODES,
  setSelectorMode,
  setSymbols,
  toggleSymbol
} from './symbolSelector.slice';

const MODE_CONFLICT_MESSAGES = {
  [SELECTOR_MODES.SPOT]: 'You have spot data in your selector. Clear selection first.',
  [SELECTOR_MODES.FORWARD_CURVE]:
    'You have forward curve data in your selector. Clear selection first.'
};

type ConflictingMode = SELECTOR_MODES.SPOT | SELECTOR_MODES.FORWARD_CURVE;

/**
 * Returns true (and narrows the type) when `current` mode conflicts with `allowed`.
 * NEUTRAL never conflicts; any other mode that differs from `allowed` does.
 */
function hasModeConflict(
  current: SELECTOR_MODES,
  allowed: SELECTOR_MODES
): current is ConflictingMode {
  return current !== SELECTOR_MODES.NEUTRAL && current !== allowed;
}

function showModeConflictSnackbar(conflictingMode: ConflictingMode): void {
  enqueueSnackbar(MODE_CONFLICT_MESSAGES[conflictingMode], {
    variant: 'warning',
    autoHideDuration: 5000
  });
}

export const selectionListener = createListenerMiddleware();

selectionListener.startListening({
  matcher: isAnyOf(addSymbol, addSymbols, setSymbols, toggleSymbol),
  effect: (action, api) => {
    const prev = (api.getOriginalState() as any).symbolSelector;
    const next = (api.getState() as any).symbolSelector;
    const isForwardMode = next.selectorMode === SELECTOR_MODES.FORWARD_CURVE;

    const prevKeys = new Set<string>(prev.symbols.map((s: any) => symbolKey(s, isForwardMode)));
    const added = (next.symbols as any[]).filter(s => !prevKeys.has(symbolKey(s, isForwardMode)));
    if (added.length) {
      // Defer analytics to avoid blocking the UI thread during interactions
      const schedule = (fn: () => void) => {
        if ('requestIdleCallback' in globalThis) {
          (globalThis as any).requestIdleCallback(() => {
            fn();
          });
        } else {
          setTimeout(fn, 0);
        }
      };
      schedule(() => {
        for (const symbol of added) {
          try {
            posthogLib.capture('symbol_select', { symbol });
          } catch {
            // non-fatal
          }
        }
      });
    }
  }
});

selectionListener.startListening({
  actionCreator: closeDrawer,
  effect: (_action, api) => {
    const { previousSymbols } = (api.getState() as any).symbolSelector;
    if (previousSymbols !== null) {
      api.dispatch(restoreSymbols());
    }
  }
});

selectionListener.startListening({
  actionCreator: requestSpotSelection,
  effect: ({ payload }, api) => {
    const { selectorMode } = (api.getState() as any).symbolSelector;
    if (hasModeConflict(selectorMode, SELECTOR_MODES.SPOT)) {
      showModeConflictSnackbar(selectorMode);
      return;
    }
    if ('symbols' in payload) {
      if (!payload.symbols.length) return;
      // Set mode first so addSymbols uses spot-mode key matching.
      api.dispatch(setSelectorMode(SELECTOR_MODES.SPOT));
      api.dispatch(addSymbols({ symbols: payload.symbols }));
    } else {
      // Set mode first so toggleSymbol uses spot-mode key matching.
      api.dispatch(setSelectorMode(SELECTOR_MODES.SPOT));
      api.dispatch(toggleSymbol({ symbol: payload.symbol }));
    }
  }
});
selectionListener.startListening({
  actionCreator: requestForwardCurveSelection,
  effect: ({ payload }, api) => {
    const { selectorMode } = (api.getState() as any).symbolSelector;
    if (hasModeConflict(selectorMode, SELECTOR_MODES.FORWARD_CURVE)) {
      showModeConflictSnackbar(selectorMode);
      return;
    }
    const { symbols } = payload;
    if (!symbols.length) return;

    // Set mode first so toggleSymbol uses isSameSymbolIgnoringPeriod for key matching.
    // Always toggle just symbols[0] as the representative for the whole curve.
    api.dispatch(setSelectorMode(SELECTOR_MODES.FORWARD_CURVE));
    api.dispatch(toggleSymbol({ symbol: symbols[0] }));
  }
});
